// Link insert dialog.
//
// Triggered by the composer's link toolbar button.
// Collects an absolute URL and an optional label; on submit, calls
// `onInsert` with the pair so the composer can splice
// `[label](href)` into the textarea at the original cursor.
//
// The dialog is intentionally minimal — it does not preview the URL,
// fetch metadata, or sanitise inputs beyond the protocol gate (which
// the rendering layer also enforces via `sanitizeLinkHref`). The
// failure modes are spelled out below the URL input so users see the
// constraint without trial-and-error.

import { useId, useRef, useState, type CSSProperties, type FormEvent } from "react";
import { X } from "lucide-react";

import { Dialog } from "~/components/Dialog";
import { GhostButton, PrimaryButton } from "~/components/MessageButtons";
import { sanitizeLinkHref } from "~/lib/markdown-render";
import { useUnderline } from "~/lib/underline";
import { ICON_STROKE } from "~/lib/icon";

export interface LinkDialogProps {
  /** Pre-fill the label with the user's current selection so the
   *  dialog reads as "format this selection as a link" rather than
   *  "type everything from scratch". */
  initialText?: string;
  /** Pre-fill the URL field. Useful for the "edit link" flow once it
   *  lands; today only the create flow consumes this. */
  initialHref?: string;
  /** Close request — Escape, backdrop, Cancel button. */
  onClose: () => void;
  /** Insert request — `href` is non-empty + protocol-allowed; `text`
   *  is whatever the user typed (may be empty, in which case the
   *  composer falls back to the original selection or "link"). */
  onInsert: (link: { href: string; text: string }) => void;
}

export function LinkDialog({
  initialText = "",
  initialHref = "",
  onClose,
  onInsert,
}: LinkDialogProps) {
  const titleId = `${useId()}-title`;
  const hrefId = `${useId()}-href`;
  const textId = `${useId()}-text`;
  const errorId = `${useId()}-error`;

  const [href, setHref] = useState(initialHref);
  const [text, setText] = useState(initialText);
  const [touched, setTouched] = useState(false);
  const hrefRef = useRef<HTMLInputElement | null>(null);
  const hrefUnderline = useUnderline();
  const textUnderline = useUnderline();

  const trimmed = href.trim();
  const sanitised = trimmed.length > 0 ? sanitizeLinkHref(trimmed) : null;
  const showError = touched && trimmed.length > 0 && sanitised === null;
  const canSubmit = sanitised !== null;

  function attemptSubmit() {
    setTouched(true);
    if (!canSubmit || !sanitised) {
      hrefRef.current?.focus();
      return;
    }
    onInsert({ href: sanitised, text: text.trim() });
  }

  function handleFormSubmit(event: FormEvent) {
    event.preventDefault();
    attemptSubmit();
  }

  return (
    <Dialog onClose={onClose} labelledBy={titleId} width="var(--chat-link-dialog-width, 420px)">
      <form onSubmit={handleFormSubmit}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--chat-space-3)",
            padding:
              "var(--chat-space-4) var(--chat-space-4) var(--chat-space-3) var(--chat-space-5)",
            borderBottom: "1px solid var(--chat-surface-border)",
          }}
        >
          <h2
            id={titleId}
            style={{
              flex: 1,
              margin: 0,
              color: "var(--chat-fg-primary)",
              fontFamily: "var(--chat-font-display)",
              fontSize: "var(--chat-type-display-sm-size)",
              lineHeight: "var(--chat-type-display-sm-line)",
              fontWeight: 500,
            }}
          >
            Insert link
          </h2>
          <button
            type="button"
            aria-label="Close"
            className="chat-button"
            onClick={onClose}
            style={{
              width: 28,
              height: 28,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
              background: "transparent",
              color: "var(--chat-fg-secondary)",
              borderRadius: "var(--chat-radius-sm)",
              cursor: "pointer",
            }}
          >
            <X size={14} strokeWidth={ICON_STROKE} />
          </button>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--chat-space-3)",
            padding: "var(--chat-space-4) var(--chat-space-5)",
          }}
        >
          <div>
            <label
              htmlFor={hrefId}
              style={{
                display: "block",
                marginBottom: "var(--chat-space-1)",
                color: "var(--chat-fg-secondary)",
                fontFamily: "var(--chat-font-sans)",
                fontSize: "var(--chat-type-body-sm-size)",
                lineHeight: "var(--chat-type-body-sm-line)",
              }}
            >
              URL
            </label>
            <input
              id={hrefId}
              ref={hrefRef}
              type="url"
              value={href}
              autoFocus
              onChange={(e) => setHref(e.target.value)}
              onFocus={hrefUnderline.onFocus}
              onBlur={() => {
                setTouched(true);
                hrefUnderline.onBlur();
              }}
              placeholder="https://example.com"
              aria-invalid={showError || undefined}
              aria-describedby={showError ? errorId : undefined}
              style={inputStyle(hrefUnderline.borderColor)}
            />
            {showError && (
              <span
                id={errorId}
                role="alert"
                style={{
                  display: "block",
                  marginTop: "var(--chat-space-1)",
                  color: "var(--chat-state-danger)",
                  fontFamily: "var(--chat-font-sans)",
                  fontSize: "var(--chat-type-body-sm-size)",
                  lineHeight: "var(--chat-type-body-sm-line)",
                }}
              >
                Use an http or https URL.
              </span>
            )}
          </div>

          <div>
            <label
              htmlFor={textId}
              style={{
                display: "block",
                marginBottom: "var(--chat-space-1)",
                color: "var(--chat-fg-secondary)",
                fontFamily: "var(--chat-font-sans)",
                fontSize: "var(--chat-type-body-sm-size)",
                lineHeight: "var(--chat-type-body-sm-line)",
              }}
            >
              Text (optional)
            </label>
            <input
              id={textId}
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onFocus={textUnderline.onFocus}
              onBlur={textUnderline.onBlur}
              placeholder="Display text"
              style={inputStyle(textUnderline.borderColor)}
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "var(--chat-space-2)",
            padding: "var(--chat-space-3) var(--chat-space-5) var(--chat-space-4)",
            borderTop: "1px solid var(--chat-surface-border)",
          }}
        >
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          {/* PrimaryButton renders type="button" so we drive submit
              through onClick. The wrapping <form>'s onSubmit still
              fires for Enter-from-an-input, so both Enter and a
              Insert click reach `attemptSubmit`. */}
          <PrimaryButton disabled={!canSubmit} onClick={attemptSubmit}>
            Insert
          </PrimaryButton>
        </div>
      </form>
    </Dialog>
  );
}

function inputStyle(borderColor: string): CSSProperties {
  return {
    width: "100%",
    height: 36,
    border: "none",
    borderBottom: `1px solid ${borderColor}`,
    background: "transparent",
    color: "var(--chat-fg-primary)",
    fontFamily: "var(--chat-font-sans)",
    fontSize: "var(--chat-type-body-md-size)",
    lineHeight: "var(--chat-type-body-md-line)",
    outline: "none",
    transition: "border-color var(--chat-motion-fast) var(--chat-motion-ease)",
  };
}
